// After correction: send the validation contract, follow its signature, then
// final-validate. Contracts are polled every 15 s while the moderator is on
// the corrected tabs.
import { useEffect, useMemo, useState } from "react";
import { contractsApi } from "@/api/contracts.api";
import { ContractStatus } from "@/lib/types/contracts";
import {
  contractProgressByTest as buildContractProgress,
  syncLocalContractProgress,
  type LocalContractProgress,
} from "../logic/moderation-logic";
import { apiErrorMessage } from "../logic/payout-logic";
import type { GetPaidBase } from "./get-paid-context";

interface ContractsFlowInput extends GetPaidBase {
  correctedTests: any[];
  validatedTests: any[];
  setCorrectedTests: (tests: any[]) => void;
  activeModTab: "PENDING" | "VALIDATED";
  activeCorrectedContractTab: ContractStatus;
  loadCorrectedTests: (contractStatus?: ContractStatus) => Promise<void>;
  loadValidatedTests: () => Promise<void>;
}

export function useModeratorContractsFlow(input: ContractsFlowInput) {
  const { t, user, toast, correctedTests, validatedTests, setCorrectedTests, activeModTab, activeCorrectedContractTab, loadCorrectedTests, loadValidatedTests } = input;
  const [moderatorContracts, setModeratorContracts] = useState<any[]>([]);
  const [localContractProgressByTest, setLocalContractProgressByTest] = useState<Record<string, LocalContractProgress>>({});
  const [finalValidationDialog, setFinalValidationDialog] = useState<{ open: boolean; test: any | null }>({ open: false, test: null });
  const [sendingContractTestId, setSendingContractTestId] = useState<string | null>(null);
  const [finalValidatingTestId, setFinalValidatingTestId] = useState<string | null>(null);

  const contractProgressByTest = useMemo(
    () => buildContractProgress(moderatorContracts, [...correctedTests, ...validatedTests], localContractProgressByTest),
    [correctedTests, localContractProgressByTest, moderatorContracts, validatedTests],
  );

  const loadModeratorContracts = async () => {
    if (user?.role !== "moderator") return;
    try {
      const contracts = await contractsApi.getMyContracts();
      setModeratorContracts(Array.isArray(contracts) ? contracts : []);
    } catch {
      setModeratorContracts([]);
    }
  };

  // Older builds cached corrected tests and contract progress locally.
  useEffect(() => {
    if (!user?.id || user?.role !== "moderator" || typeof window === "undefined") {
      return;
    }
    window.localStorage.removeItem(`yodi-moderator-corrected-tests:${user.id}`);
    window.localStorage.removeItem(`yodi-moderator-contract-progress:${user.id}`);
    setCorrectedTests([]);
    setLocalContractProgressByTest({});
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (!moderatorContracts.length) {
      return;
    }
    setLocalContractProgressByTest((prev) => syncLocalContractProgress(prev, [...correctedTests, ...validatedTests], moderatorContracts));
  }, [correctedTests, moderatorContracts, validatedTests]);

  useEffect(() => {
    if (user?.role !== "moderator" || activeModTab !== "VALIDATED") {
      return;
    }
    const intervalId = window.setInterval(() => {
      void loadModeratorContracts();
    }, 15000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeModTab, user?.role]);

  const handleSendContractForTest = async (test: any) => {
    const userId = Number(test?.userId);
    if (!Number.isFinite(userId)) {
      toast({ title: t.getPaid.userNotFound, description: t.getPaid.sendContractFailed, variant: "destructive" });
      return;
    }

    setSendingContractTestId(String(test.id));
    try {
      const sentContract = await contractsApi.sendValidationContract(userId);
      setLocalContractProgressByTest((prev) => ({
        ...prev,
        [String(test.id)]: {
          contractId: String(sentContract?.id || prev[String(test.id)]?.contractId || ""),
          userId: String(test?.userId || ""),
          sent: true,
          signed: false,
          finalValidated: false,
        },
      }));
      setModeratorContracts((prev: any[]) => {
        const nextContract = {
          ...sentContract,
          status: String(sentContract?.status || ContractStatus.SENT).toLowerCase(),
          assignedTo: sentContract?.assignedTo || { id: String(test?.userId), name: test?.userName || "" },
        };
        const existingContracts = prev.filter((item: any) => String(item?.id) !== String(nextContract?.id));
        return [nextContract, ...existingContracts];
      });
      await loadModeratorContracts();
      if (activeModTab === "VALIDATED") {
        await loadCorrectedTests(activeCorrectedContractTab);
      }
      toast({ title: t.getPaid.contractSent, description: t.getPaid.contractSentDesc.replace("{name}", test?.userName || "l'utilisateur") });
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.sendContractRequestFailed), variant: "destructive" });
    } finally {
      setSendingContractTestId(null);
    }
  };

  const handleConfirmFinalValidate = async () => {
    const test = finalValidationDialog.test;
    if (!test) {
      return;
    }

    const progress = contractProgressByTest[String(test.id)];
    const totalScore = Number(test?.totalScore);
    if (!progress?.signed || !progress?.contractId || !Number.isFinite(totalScore)) {
      setFinalValidationDialog({ open: false, test: null });
      return;
    }

    setFinalValidatingTestId(String(test.id));
    try {
      await contractsApi.finalValidateContract(progress.contractId);
      await loadCorrectedTests(activeCorrectedContractTab);
      await loadValidatedTests();
      await loadModeratorContracts();
      toast({ title: t.getPaid.finalValidationSuccess, description: t.getPaid.finalValidationSuccessDesc.replace("{name}", test?.userName || "cet utilisateur") });
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.finalValidationFailed), variant: "destructive" });
    } finally {
      setFinalValidatingTestId(null);
      setFinalValidationDialog({ open: false, test: null });
    }
  };

  return {
    moderatorContracts, loadModeratorContracts, contractProgressByTest,
    finalValidationDialog, setFinalValidationDialog,
    sendingContractTestId, finalValidatingTestId,
    handleSendContractForTest, handleConfirmFinalValidate,
  };
}
